import React, { useEffect, useState } from "react";
import { useLang } from "../context/LangContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { MessageSquare, Send, Lightbulb, AlertTriangle, Clock, CheckCircle2 } from "lucide-react";

export default function Feedback() {
  const { t } = useLang();
  const [kind, setKind] = useState("suggestion");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/feedback/mine");
      setHistory(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) return toast.error(t("fields_required"));
    setLoading(true);
    try {
      await api.post("/feedback", { kind, subject, message });
      toast.success(t("feedback_sent"));
      setSubject("");
      setMessage("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="feedback-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold flex items-center gap-2">
          <MessageSquare className="text-[#2A5948]" size={26} />
          {t("feedback")}
        </h1>
        <p className="text-sm text-[#5C6053] mt-1">{t("feedback_desc")}</p>
      </div>

      <form onSubmit={submit} className="bg-white border border-[#E5E1D8] rounded-2xl p-6 shadow-sm space-y-4">
        <div>
          <div className="text-xs font-medium text-[#5C6053] mb-2">{t("feedback_kind")}</div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              data-testid="feedback-kind-suggestion"
              onClick={() => setKind("suggestion")}
              className={`px-4 py-3 rounded-xl text-sm border-2 inline-flex items-center justify-center gap-2 transition-colors ${
                kind === "suggestion" ? "bg-[#E6F1E7] border-[#2A5948] text-[#2A5948]" : "bg-white border-[#E5E1D8] text-[#5C6053]"
              }`}
            >
              <Lightbulb size={16} /> {t("feedback_suggestion")}
            </button>
            <button
              type="button"
              data-testid="feedback-kind-complaint"
              onClick={() => setKind("complaint")}
              className={`px-4 py-3 rounded-xl text-sm border-2 inline-flex items-center justify-center gap-2 transition-colors ${
                kind === "complaint" ? "bg-[#FBE9E4] border-[#C96A52] text-[#C96A52]" : "bg-white border-[#E5E1D8] text-[#5C6053]"
              }`}
            >
              <AlertTriangle size={16} /> {t("feedback_complaint")}
            </button>
          </div>
        </div>

        <label className="block">
          <div className="text-xs font-medium text-[#5C6053] mb-1">{t("feedback_subject")}</div>
          <input
            data-testid="feedback-subject-input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={200}
            className="w-full border border-[#E5E1D8] bg-[#F9F6F0] rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#2A5948]/40"
          />
        </label>

        <label className="block">
          <div className="text-xs font-medium text-[#5C6053] mb-1">{t("feedback_message")}</div>
          <textarea
            data-testid="feedback-message-input"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            maxLength={4000}
            className="w-full border border-[#E5E1D8] bg-[#F9F6F0] rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#2A5948]/40 resize-none"
          />
        </label>

        <div className="flex justify-end">
          <button
            data-testid="feedback-submit-btn"
            type="submit"
            disabled={loading}
            className="brand-btn px-5 py-2.5 rounded-xl text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60"
          >
            <Send size={16} />
            {loading ? "..." : t("feedback_send")}
          </button>
        </div>
      </form>

      <div>
        <h2 className="text-lg font-semibold mb-3">{t("feedback_my_history")}</h2>
        {history.length === 0 ? (
          <div className="bg-white border border-[#E5E1D8] rounded-2xl p-8 text-center text-sm text-[#5C6053]" data-testid="feedback-empty">
            —
          </div>
        ) : (
          <div className="space-y-3">
            {history.map((f) => (
              <div key={f.id} data-testid={`feedback-item-${f.id}`} className="bg-white border border-[#E5E1D8] rounded-2xl p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${f.kind === "suggestion" ? "bg-[#E6F1E7] text-[#2A5948]" : "bg-[#FBE9E4] text-[#C96A52]"}`}>
                        {f.kind === "suggestion" ? <Lightbulb size={10} /> : <AlertTriangle size={10} />}
                        {t(`feedback_${f.kind}`)}
                      </span>
                      <h3 className="font-medium">{f.subject}</h3>
                    </div>
                    <p className="text-sm text-[#5C6053] mt-2 whitespace-pre-wrap">{f.message}</p>
                    <div className="text-xs text-[#5C6053] mt-2">{f.created_at?.slice(0, 16).replace("T", " ")}</div>
                  </div>
                  <span className={`text-xs inline-flex items-center gap-1 px-2 py-0.5 rounded flex-shrink-0 ${f.status === "resolved" ? "bg-[#E6F1E7] text-[#2A5948]" : "bg-[#FFF3D6] text-[#8A6D1F]"}`}>
                    {f.status === "resolved" ? <CheckCircle2 size={10} /> : <Clock size={10} />}
                    {t(`feedback_status_${f.status}`)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
